import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import {
  OutputFormulaDeleteResult,
  OutputFormulaService,
} from 'src/app/services/output-formulas/output-formula.service';
import { OutputFormula } from 'src/app/models/output-formulas/output-formula';

export interface OutputFormulaDeleteDialogData {
  organizationId: string;
  current: OutputFormula;
}

interface DependentsWarning {
  count: number;
  names: string[];
}

@Component({
  selector: 'app-output-formula-delete-dialog',
  templateUrl: './output-formula-delete-dialog.component.html',
  styleUrls: ['./output-formula-delete-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class OutputFormulaDeleteDialogComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly acknowledgeControl = new FormControl<boolean>(false, { nonNullable: true });

  warning: DependentsWarning | null = null;
  probed = false;
  submitting = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: OutputFormulaDeleteDialogData,
    private readonly dialogRef: MatDialogRef<OutputFormulaDeleteDialogComponent, boolean>,
    private readonly service: OutputFormulaService,
  ) {
    this.probeDependents();
  }

  get needsAcknowledge(): boolean {
    return this.warning !== null && this.warning.count > 0;
  }

  get canDelete(): boolean {
    if (this.submitting || !this.probed) return false;
    if (this.needsAcknowledge && !this.acknowledgeControl.value) return false;
    return true;
  }

  confirm(): void {
    if (!this.canDelete) return;
    this.submitting = true;
    this.service
      .remove(this.data.organizationId, this.data.current.id, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.dialogRef.close(true),
        error: () => {
          this.submitting = false;
        },
      });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  private probeDependents(): void {
    this.service
      .remove(this.data.organizationId, this.data.current.id, false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: OutputFormulaDeleteResult) => {
          this.warning = result.warning;
          this.probed = true;
        },
        error: () => {
          this.probed = true;
        },
      });
  }
}
